import React, { useState } from "react";

import SimRatingForm from "./SimRatingForm";
import "./SimFeedback.css";
import { useLanguage } from "../../i18n/LanguageContext";

/**
 * Both fields come straight from esimaccess /api/v1/open/esim/query, which
 * getEsimBySimId already returns.
 *
 *   installationTime  a timestamp, or null if the profile was never installed
 *   smdpStatus        RELEASED -> DOWNLOAD -> INSTALLATION -> ENABLED/DISABLED -> DELETED
 *
 * installationTime is the stronger signal — definitive rather than inferred —
 * so it decides "installed"; smdpStatus only refines what went wrong while it
 * is still null. Together they separate a customer stuck on installation from
 * one who installed fine and simply has not travelled yet.
 */
// Copy for each stage lives in installCard.stages.<STAGE>.

export const stageFor = (smdpStatus, installationTime) => {
    const s = String(smdpStatus || "").trim().toUpperCase();

    // The profile was installed and then removed from the device.
    if (s === "DELETED") return "DELETED";

    // A timestamp here is proof it installed, whatever smdpStatus now says.
    if (installationTime) return "INSTALLED";

    if (s === "DOWNLOAD" || s === "INSTALLATION") return "STALLED";

    // No installationTime and no progress past RELEASED: never started.
    return "NOT_STARTED";
};

export default function SimInstallCard({ simId, smdpStatus, installationTime, existing }) {
    const [dismissed, setDismissed] = useState(false);
    const { t } = useLanguage();

    if (dismissed) return null;

    const stage = stageFor(smdpStatus, installationTime);
    const copy = t(`installCard.stages.${stage}`);

    return (
        <div className="ed-card ed-fb">
            <div className="ed-fb-head">
                <div className="ed-fb-headtext">
                    <h2 className="ed-fb-title">{copy.title}</h2>
                    <p className="ed-fb-body">{copy.body}</p>
                </div>
                <button
                    type="button"
                    className="ed-fb-dismiss"
                    onClick={() => setDismissed(true)}
                    aria-label={t("common.close")}
                >
                    ×
                </button>
            </div>

            {/* Asked at every stage. Someone who never got the profile onto
                their phone has just as much to say about the process as someone
                who finished it — often more. */}
            <SimRatingForm
                simId={simId}
                kind="INSTALL"
                smdpStatus={smdpStatus}
                question={t("installCard.question")}
                existing={existing}
            />
        </div>
    );
}
